import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RequestsTable = ({ requests, onRequestSelect, selectedRequest, onStatusFilter, statusFilter }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'submissionDate', direction: 'desc' });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedRequests = [...requests]?.sort((a, b) => {
    if (sortConfig?.key === 'submissionDate') {
      return sortConfig?.direction === 'asc' 
        ? new Date(a.submissionDate) - new Date(b.submissionDate)
        : new Date(b.submissionDate) - new Date(a.submissionDate);
    }
    
    const aValue = a?.[sortConfig?.key]?.toString()?.toLowerCase() || '';
    const bValue = b?.[sortConfig?.key]?.toString()?.toLowerCase() || '';
    
    if (sortConfig?.direction === 'asc') {
      return aValue?.localeCompare(bValue);
    }
    return bValue?.localeCompare(aValue);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-warning/10 text-warning border-warning/20';
      case 'approved': return 'bg-success/10 text-success border-success/20';
      case 'rejected': return 'bg-error/10 text-error border-error/20';
      case 'in-review': return 'bg-accent/10 text-accent border-accent/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error';
      case 'medium': return 'text-warning';
      case 'low': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortIcon = ({ column }) => {
    if (sortConfig?.key !== column) {
      return <Icon name="ArrowUpDown" size={14} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ArrowUp" size={14} className="text-primary" />
      : <Icon name="ArrowDown" size={14} className="text-primary" />;
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Header with filters */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-foreground">Change Requests</h3>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => onStatusFilter(e?.target?.value)}
              className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in-review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/20">
            <tr>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">
                <button
                  onClick={() => handleSort('requestId')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Request ID
                  <SortIcon column="requestId" />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">
                <button
                  onClick={() => handleSort('documentName')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Document
                  <SortIcon column="documentName" />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">
                <button
                  onClick={() => handleSort('requester')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Requester
                  <SortIcon column="requester" />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">
                <button
                  onClick={() => handleSort('submissionDate')}
                  className="flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  Submitted
                  <SortIcon column="submissionDate" />
                </button>
              </th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">Priority</th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">Status</th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">Reviewer</th>
              <th className="text-left p-3 font-medium text-sm text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRequests?.map((request) => (
              <tr
                key={request?.id}
                onClick={() => onRequestSelect(request)}
                className={`border-b border-border hover:bg-muted/30 cursor-pointer transition-colors ${
                  selectedRequest?.id === request?.id ? 'bg-accent/10' : ''
                }`}
              >
                <td className="p-3">
                  <span className="font-mono text-sm text-primary font-medium">
                    {request?.requestId}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={16} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground truncate max-w-48">
                      {request?.documentName}
                    </span>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center">
                      <Icon name="User" size={12} color="white" />
                    </div>
                    <span className="text-sm text-foreground">{request?.requester}</span>
                  </div>
                </td>
                <td className="p-3">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(request?.submissionDate)}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Icon 
                      name="AlertCircle" 
                      size={14} 
                      className={getPriorityColor(request?.priority)}
                    />
                    <span className={`text-sm capitalize ${getPriorityColor(request?.priority)}`}>
                      {request?.priority}
                    </span>
                  </div>
                </td>
                <td className="p-3">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(request?.status)}`}>
                    {request?.status?.replace('-', ' ')}
                  </span>
                </td>
                <td className="p-3">
                  {request?.reviewer ? (
                    <span className="text-sm text-foreground">{request?.reviewer}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground">Unassigned</span>
                  )}
                </td>
                <td className="p-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Eye"
                    iconSize={14}
                    onClick={(e) => {
                      e?.stopPropagation();
                      onRequestSelect(request);
                    }}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedRequests?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Change Requests</h3>
          <p className="text-sm text-muted-foreground">
            {statusFilter === 'all' ?'No change requests have been submitted yet.'
              : `No ${statusFilter} change requests found.`
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default RequestsTable;